import { CoreImageView } from '@core/widgets';
import { camelCaseToKebabCase, getFieldName, useBehavior, useCheckBox } from '@libs/core';
import { AssetsSvgMap, TInputData, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, useId } from 'react';
import { TCheckboxOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { getHref } from '../Image/imageUtils';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './checkBoxSwitcher.module.css';

type TCheckboxProps<TD, TO> =
    | {
          isWidget: true;
          widget: TWidget<TD, TO>;
      }
    | ({
          isWidget: false;
          uid?: string;
          value: boolean;
          handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
      } & Pick<TInputData, 'tabIndex' | 'messages' | 'label'> &
          TCheckboxOption);

const Checkbox = (props: TCheckboxProps<TInputData, TCheckboxOption>) => {
    const { isWidget } = props;
    const finalWidget = !isWidget
        ? ({
              uid: props.uid ?? '',
              data: {
                  value: props.value,
                  tabIndex: props.tabIndex,
                  label: props.label,
                  messages: props.messages,
              },
              options: {
                  isDisabled: props.isDisabled,
                  margin: props.margin,
                  autofill: props.autofill,
              },
          } as TWidget<TInputData, TCheckboxOption>)
        : props.widget;

    const id = useId();
    const data = finalWidget.data ?? {};
    const options = finalWidget.options ?? {};
    const label = data.messages?.label || data.label;
    const { handleChange, isChecked } = useCheckBox(finalWidget, { skipValidation: true });
    const { isDisabled, isHidden } = useBehavior(finalWidget);

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div data-test={finalWidget.uid} style={getInlineStyles({ margin: options.margin })}>
                <label className={clsx(styles.checkboxLabel, isDisabled && styles.disabled)} htmlFor={id}>
                    <input
                        data-test={`input_${finalWidget.uid}`}
                        className={styles.checkboxInput}
                        id={id}
                        name={getFieldName(options.autofill)}
                        autoComplete={camelCaseToKebabCase(options.autofill)}
                        type="checkbox"
                        onChange={(e) => {
                            if (isWidget) {
                                handleChange(e);
                            } else {
                                props.handleChange(e);
                            }
                        }}
                        checked={isChecked}
                        disabled={isDisabled}
                        tabIndex={data.tabIndex}
                    />
                    <span className={styles.checkboxIndicator}>
                        <span className={styles.checkboxTickWrap}>
                            <CoreImageView
                                src={getHref(AssetsSvgMap.ic_check_20, finalWidget.version)}
                                styleProps={{
                                    width: 20,
                                    height: 20,
                                    iconColor: `var(--white_base)`,
                                }}
                            />
                        </span>
                    </span>
                    {label && (
                        <TextCommon
                            isWidget={false}
                            content={label}
                            options={{
                                textColor: isDisabled ? 'grey_16_lc' : 'grey_72_lc',
                                margin: { left: 16 },
                            }}
                        />
                    )}
                </label>
            </div>
        </Activity>
    );
};
export { Checkbox };
